import React, { FC, useState, useCallback, useEffect } from 'react'
import cn from 'classnames'
import { ListChildComponentProps } from 'react-window'
import ClickOutside from '../../components/ClickOutside'
import ProductPhoto from '../../components/ProductPhoto'
import BinIcon from '../../icons/bin.svg'
import { ICartItem } from '../../types/orders'
import { money } from '../../common/display'
import StockState from './StockState'

const ShoppingCartRow: FC<ListChildComponentProps> = ({
  data,
  index,
  style,
}) => {
  const [focused, setFocused] = useState<boolean>(false)
  const cartItem: ICartItem = data.cartItem || data.cartItems[index]

  const draft = data.drafts[cartItem.id]
  const value = draft != null ? draft : cartItem.amountRequested
  const total = money(+(value || 0) * +(cartItem.effectivePrice || 0))
  const price = `${money(cartItem.effectivePrice || 0)} / ${
    cartItem.effectiveUnit || ''
  }`
  const isUnavailable = cartItem.product.unavailable
  const canDelete = data.editable
  const isEditable = data.editable && !isUnavailable
  const multiCatalogEnabled = data.multiCatalogEnabled
  const isLastUnavailable =
    data.unavailableCount > 0 && index === data.unavailableCount - 1
  const handleDelete = useCallback(() => {
    if (!confirm('Are you sure you want to remove the item from cart?')) return
    data.onDelete(cartItem)
  }, [data.onDelete, cartItem])

  useEffect(() => {
    if (data.autoEditIndex !== index) return
    data.onAutoEditHandled?.()
    if (isEditable) setFocused(true)
  }, [data.autoEditIndex, index, isEditable])

  const showPalletQuantity = (cartItem.product.palletQuantity ?? 0) > 0
  const showCaseQuantity = (cartItem.product.caseQuantity ?? 0) > 0

  return (
    <div
      className={cn('order-modal_list-item', {
        '-last-unavailable': isLastUnavailable,
      })}
      style={style}
      data-testid={`cart-row-${cartItem.product.code}`}
    >
      <div className="order-modal_list-product order-snippet -small">
        <div className="order-snippet_col -info -large">
          <div className="order-snippet_image-wrapper">
            <ProductPhoto
              photo={cartItem.product.photo}
              className="order-snippet_image"
            />
          </div>
          <div
            className={cn('order-snippet_title', {
              '-unavailable': isUnavailable,
            })}
          >
            {cartItem.product.name}
          </div>
          <div className="order-snippet_info">
            <div className="order-snippet_sku">{cartItem.product.code}</div>
            {multiCatalogEnabled && (
              <div className="order-snippet_erp-catalog">
                {cartItem.product.erpCatalogName}
              </div>
            )}
          </div>
          <div className="order-snippet_price">{price}</div>
          {(showPalletQuantity || showCaseQuantity) && (
            <div className="order-snippet_packaging">
              {[
                showPalletQuantity &&
                  `${cartItem.product.palletQuantity} / PALLET`,
                showCaseQuantity && `${cartItem.product.caseQuantity} / CASE`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
          )}
        </div>
        {canDelete && (
          <div
            className="order-snippet_action"
            onClick={handleDelete}
            data-testid={`cart-remove-${cartItem.product.code}`}
          >
            <BinIcon />
          </div>
        )}
      </div>
      <div
        className={cn('tile -bordered', {
          'clear-button': isEditable,
          '-done': !isEditable,
        })}
        onClick={() => !focused && isEditable && setFocused(true)}
      >
        {focused ? (
          <ClickOutside
            onClickOutside={() => {
              setFocused(false)
              data.onAmountFlush(cartItem)
            }}
            className="tile_count"
          >
            <input
              id={`cart-qty-input-${cartItem.product.code}`}
              type="number"
              value={value.toString()}
              onChange={(e) => data.onAmountChange(cartItem, e.target.value)}
              onFocus={(e) => e.target.select()}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' && e.key !== 'Tab') return
                data.onAmountFlush(cartItem)
                if (e.key === 'Tab') {
                  e.preventDefault()
                  setFocused(false)
                  data.onFocusNextRow?.(index)
                }
              }}
              className="tile_count-input"
              autoFocus
            />
          </ClickOutside>
        ) : (
          <button
            className="tile_count-toggle clear-button"
            data-testid={`cart-qty-${cartItem.product.code}`}
            onKeyDown={(e) => {
              if (e.key === 'Tab' && !e.shiftKey) {
                e.preventDefault()
                data.onFocusNextRow?.(index)
              }
            }}
          >
            {value}
          </button>
        )}
        {isEditable &&
          cartItem.product.stockState &&
          cartItem.product.stockInventory !== undefined && (
            <StockState
              state={cartItem.product.stockState}
              inventory={cartItem.product.stockInventory}
            />
          )}
      </div>
      <div className="tile -done -bordered">{total}</div>
    </div>
  )
}

export default ShoppingCartRow
